import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { coursesApi, paymentsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  CreditCard, 
  ArrowLeft,
  Check,
  Clock,
  Users,
  Star,
  User,
  Mail,
  CreditCard as IdCard
} from 'lucide-react';

interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail?: string;
  price: number;
  duration: number;
  studentsCount: number;
  category: {
    name: string;
  };
}

const PaymentPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { student } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    idNumber: ''
  });

  useEffect(() => {
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      // Usar la API pública para obtener información del curso
      const response = await coursesApi.getAll();
      const allCourses = response.data.data?.courses || [];
      const courseData = allCourses.find((c: any) => c.id === courseId);
      
      if (!courseData) {
        // Si no se encuentra en la lista pública, intentar con la API específica
        try {
          const directResponse = await fetch(`https://api.escuela.agentesia.cloud/api/v1/courses/${courseId}`);
          const directData = await directResponse.json();
          if (directData.success) {
            setCourse(directData.data);
            return;
          }
        } catch (directError) {
          console.log('Direct API call failed, course not found');
        }
        setError('Curso no encontrado');
        return;
      }
      
      setCourse(courseData);
    } catch (error) {
      console.error('Error loading course:', error);
      setError('Error al cargar el curso');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar formulario
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.idNumber) {
      alert('Por favor completa todos los campos');
      return;
    }

    try {
      setPaymentLoading(true);
      
      // Crear pago WOMPI
      const response = await paymentsApi.createWompiCoursePayment(courseId!, {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        idNumber: formData.idNumber
      });

      if (response.data.success && response.data.checkoutUrl) {
        // Redirigir a WOMPI
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('Error al crear el pago');
      }
    } catch (error: any) {
      console.error('Error procesando pago:', error);
      alert(error.response?.data?.message || 'Error al procesar el pago. Intenta nuevamente.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400">{error || 'Curso no encontrado'}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
        >
          <ArrowLeft size={16} />
          Volver al Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className={`${!student ? 'min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900' : ''}`}>
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600/20 via-purple-600/10 to-cyan-600/20 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
          {student && (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={20} />
              Volver al Dashboard
            </button>
          )}
        
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">
            Inscríbete al Curso
          </h1>
          <p className="text-slate-300 text-lg">
            Completa tu inscripción y comienza tu aprendizaje
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Course Info */}
        <div className="space-y-6">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            {course.thumbnail ? (
              <img
                src={course.thumbnail}
                alt={course.title}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
            ) : (
              <div className="w-full h-48 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center mb-4">
                <CreditCard className="text-white" size={48} />
              </div>
            )}
            
            <div className="mb-4">
              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">
                {course.category?.name || 'Sin categoría'}
              </span>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-3">{course.title}</h2>
            <p className="text-slate-300 mb-6">{course.description}</p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                <Clock className="text-blue-400 mx-auto mb-1" size={20} />
                <p className="text-white font-semibold">{Math.ceil(course.duration / 60)}</p>
                <p className="text-slate-400 text-xs">Semanas</p>
              </div>
              <div className="text-center p-3 bg-emerald-500/10 rounded-lg">
                <Users className="text-emerald-400 mx-auto mb-1" size={20} />
                <p className="text-white font-semibold">{course.studentsCount}</p>
                <p className="text-slate-400 text-xs">Estudiantes</p>
              </div>
              <div className="text-center p-3 bg-purple-500/10 rounded-lg">
                <Star className="text-purple-400 mx-auto mb-1" size={20} />
                <p className="text-white font-semibold">4.9</p>
                <p className="text-slate-400 text-xs">Rating</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
          <h3 className="text-2xl font-bold text-white mb-6">Inscribirse al Curso</h3>
          
          {/* Price Summary */}
          <div className="space-y-4 p-6 bg-slate-800/50 rounded-lg border border-slate-700/50 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-white font-semibold">{course.title}</p>
                <p className="text-slate-400 text-sm">Acceso completo</p>
              </div>
              <span className="text-2xl font-bold text-white">${course.price.toLocaleString()}</span>
            </div>
            
            <div className="border-t border-slate-600 pt-4">
              <div className="flex justify-between items-center text-xl font-bold">
                <span className="text-white">Total</span>
                <span className="text-2xl font-bold text-blue-400">
                  ${course.price.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <form onSubmit={handlePayment} className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <Mail size={16} className="inline mr-2" />
                Correo electrónico
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="tu@ejemplo.com"
                className="w-full p-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <User size={16} className="inline mr-2" />
                  Nombre
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Tu nombre"
                  className="w-full p-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <User size={16} className="inline mr-2" />
                  Apellido
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Tu apellido"
                  className="w-full p-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                <IdCard size={16} className="inline mr-2" />
                Cédula
              </label>
              <input
                type="text"
                name="idNumber"
                value={formData.idNumber}
                onChange={handleInputChange}
                placeholder="Número de cédula"
                className="w-full p-3 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-blue-500 focus:outline-none"
                required
              />
            </div>

            {/* Payment Button */}
            <button
              type="submit"
              disabled={paymentLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paymentLoading ? (
                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <CreditCard size={20} />
                  PAGAR ${course.price.toLocaleString()}
                </>
              )}
            </button>
          </form>

          <div className="text-center mb-6">
            <p className="text-slate-400 text-sm">
              🔒 Pago seguro con WOMPI
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Check size={16} className="text-green-400" />
              <span className="text-slate-300">Acceso de por vida</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Check size={16} className="text-green-400" />
              <span className="text-slate-300">Certificado de finalización</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Check size={16} className="text-green-400" />
              <span className="text-slate-300">Soporte del instructor</span>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;